import cx from "classnames";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Form } from "react-bootstrap";
import PropTypes from "prop-types";
import { useFormContext } from "react-hook-form";
import { isEmpty } from "ramda";
import { RangeFieldController, FieldController } from "./FieldController";
import { isEmptyOrNull, lodashUtils } from "../../../utils";
import { IFXErrorAlert } from "../IFXAlert";

const rangeFields = ["numberRange", "range"];

const selectedValueFields = [
  "range",
  "autosuggest",
  "datepicker",
  "timepicker",
  "dropdown",
];
const DEFAULT_CLASSNAME = "ifx-field-wrapper";

const RangeFieldWrapper = React.memo(props => {
  const {
    children: field,
    className,
    marginClassName,
    controlId,
    label,
    type,
    value,
    selected,
    required,
    errorMessage = {},
    isInvalid,
    onChange,
    alwaysFloat,
    defaultValue,
    name,
    rules,
    ..._props
  } = props;

  //console.log("RangeFieldWrapper _props:", _props);

  const [wrapperState, setWrapperState] = useState({
    isFocused: false,
    from: {
      isFocused: false,
      errorMessage: errorMessage.from,
    },
    to: { isFocused: false, errorMessage: errorMessage.to },
  });

  //Required for Error Animation
  useEffect(() => {
    let _state = { ...wrapperState };
    if (!!errorMessage.common) {
      _state = {
        ..._state,
        to: { ...wrapperState.to, errorMessage: undefined },
        from: { ...wrapperState.from, errorMessage: undefined },
      };
      if (!lodashUtils.isEqual(wrapperState, _state)) setWrapperState(_state);
    } else {
      if (
        !!errorMessage.from &&
        errorMessage.from !== wrapperState.from.errorMessage
      ) {
        _state = {
          ..._state,
          from: { ...wrapperState.from, errorMessage: errorMessage.from },
        };
        if (!lodashUtils.isEqual(wrapperState, _state)) setWrapperState(_state);
      }

      if (
        !!errorMessage.to &&
        errorMessage.to !== wrapperState.to.errorMessage
      ) {
        _state = {
          ..._state,
          to: { ...wrapperState.to, errorMessage: errorMessage.to },
        };
        if (!lodashUtils.isEqual(wrapperState, _state)) setWrapperState(_state);
      }
    }
  }, [setWrapperState, wrapperState, errorMessage]);

  const _value = selected || value || {};

  const _alwaysFloat = alwaysFloat || [
    !isEmptyOrNull(_value.from),
    !isEmptyOrNull(_value.to),
  ];

  const onFocus = useCallback(
    [
      () => {
        if (!wrapperState.from.isFocused) {
          setWrapperState({
            ...wrapperState,
            isFocused: true,
            from: { ...wrapperState.form, isFocused: true },
          });
        }
        if (field.props.onFocus) {
          if (Array.isArray(field.props.onFocus)) {
            field.props.onFocus[0]();
          } else {
            field.props.onFocus();
          }
        }
      },
      () => {
        if (!wrapperState.to.isFocused) {
          setWrapperState({
            ...wrapperState,
            isFocused: true,
            to: { ...wrapperState.to, isFocused: true },
          });
        }
        if (field.props.onFocus) {
          if (Array.isArray(field.props.onFocus)) {
            field.props.onFocus[1]();
          } else {
            field.props.onFocus();
          }
        }
      },
    ],
    [field, wrapperState, setWrapperState]
  );

  const onBlur = useCallback(
    [
      e => {
        if (wrapperState.from.isFocused) {
          setWrapperState({
            ...wrapperState,
            isFocused: wrapperState.to.isFocused,
            from: { ...wrapperState.form, isFocused: false },
          });
        }
        if (field.props.onBlur) {
          if (Array.isArray(field.props.onBlur)) {
            field.props.onBlur[0](e);
          } else {
            field.props.onBlur(e);
          }
        }
      },
      e => {
        if (wrapperState.to.isFocused) {
          setWrapperState({
            ...wrapperState,
            isFocused: wrapperState.from.isFocused,
            to: { ...wrapperState.to, isFocused: false },
          });
        }
        if (field.props.onBlur) {
          if (Array.isArray(field.props.onBlur)) {
            field.props.onBlur[1](e);
          } else {
            field.props.onBlur(e);
          }
        }
      },
    ],

    [field, wrapperState, setWrapperState]
  );

  const onCalendarClose = useCallback(
    [
      e => {
        //console.log("onCalendarCloseFrom");
        if (wrapperState.from.isFocused) {
          setWrapperState({
            ...wrapperState,
            isFocused: wrapperState.to.isFocused,
            from: { ...wrapperState.form, isFocused: false },
          });
        }
        if (field.props.onCalendarClose) {
          if (Array.isArray(field.props.onCalendarClose)) {
            field.props.onCalendarClose[0](e);
          } else {
            field.props.onCalendarClose(e);
          }
        }
      },
      e => {
        //console.log("onCalendarCloseTo");
        if (wrapperState.to.isFocused) {
          setWrapperState({
            ...wrapperState,
            isFocused: wrapperState.from.isFocused,
            to: { ...wrapperState.to, isFocused: false },
          });
        }
        if (field.props.onCalendarClose) {
          if (Array.isArray(field.props.onCalendarClose)) {
            field.props.onCalendarClose[1](e);
          } else {
            field.props.onCalendarClose(e);
          }
        }
      },
    ],

    [field, wrapperState, setWrapperState]
  );

  let field_new;
  if (field) {
    let {
      props: { inputGroupClassName },
    } = field;

    field_new = [field].map((child, idx) =>
      React.cloneElement(child, {
        ..._props,
        ...child.props,
        key: idx,
        onFocus,
        onBlur,
        ...(type === "range" && {
          onCalendarClose,
        }),
        controlId,
        required,
        alwaysFloat: _alwaysFloat,
        isInvalid,
        label,
        wrapperState,
        ...((selected && { selected }) || { value }),
        ...(onChange && { onChange }),
        inputGroupClassName: cx(inputGroupClassName, {
          focused: wrapperState.isFocused,
        }),
      })
    );
  }
  const children = field_new;

  return (
    <>
      <Form.Group
        className={cx(
          DEFAULT_CLASSNAME,
          "range-field",
          props.className,
          marginClassName ? marginClassName : ""
          /* {
            "is-invalid": isInvalid,
          } */
        )}
        {..._props}
      >
        <div className="ifx-position-relative">
          {children}
          {
            <div className={`${(!!errorMessage.common && "is-invalid") || ""}`}>
              <Form.Control.Feedback type="d-flex justify-content-center ifx-field-error invalid">
                {errorMessage.common}
              </Form.Control.Feedback>
            </div>
          }
        </div>
      </Form.Group>
    </>
  );
});

const RegularFieldWrapper = React.memo(props => {
  const {
    children: field,
    className,
    marginClassName,
    controlId,
    label,
    type,
    isGroup = false,
    value,
    selected,
    required,
    errorMessage,
    isInvalid,
    onChange,
    alwaysFloat,
    defaultValue,
    name,
    rules,
    options,
    labelClassName,
    labelHidden = false,
    externalErrorHandler,
    ..._props
  } = props;

  //console.log("RegularFieldWrapper props:", props);
  //console.log("RegularFieldWrapper name:", name);
  /* console.log(
    `RegularFieldWrapper controlId:${controlId} value:`,
    value,
    "defaultValue:",
    defaultValue
  ); */
  //console.log("RegularFieldWrapper onChange:", field.props.onChange);
  //console.log("RegularFieldWrapper type:", type);

  const [wrapperState, setWrapperState] = useState({
    isFocused: false,
    errorMessage: errorMessage,
  });

  /* console.log(
    `RegularFieldWrapper controlId:${controlId} wrapperState:`,
    wrapperState
  ); */

  //Required for Error Animation
  useEffect(() => {
    if (!!errorMessage && errorMessage !== wrapperState.errorMessage)
      setWrapperState({ ...wrapperState, errorMessage });
  }, [setWrapperState, wrapperState, errorMessage]);

  useEffect(() => {
    externalErrorHandler &&
      externalErrorHandler({
        errorMessage: wrapperState.errorMessage,
        isInvalid,
      });
  }, [externalErrorHandler, wrapperState.errorMessage, isInvalid]);

  const _value = selected || field.props.value || value;
  //console.log(`RegularFieldWrapper controlId:${controlId} _value:`, _value);
  //console.log("value", value);

  const floatLabel = labelHidden
    ? false
    : alwaysFloat ||
      wrapperState.isFocused ||
      (type === "dropdown"
        ? !isEmptyOrNull(
            _value && (typeof _value === "string" || Array.isArray(_value))
              ? _value
              : !!_value?.label
              ? _value?.label
              : _value?.value
          )
        : !isEmptyOrNull(_value)); //autosuggest

  /* console.log(
    `RegularFieldWrapper controlId:${controlId} floatLabel:`,
    floatLabel
  ); */

  const onFocus = useCallback(
    event => {
      if (!wrapperState.isFocused) {
        setWrapperState({ ...wrapperState, isFocused: true });
      }
      //console.log("regular field onFocus", onFocus);
      if (field.props.onFocus) field.props.onFocus(event);
    },
    [field, wrapperState, setWrapperState]
  );

  const onBlur = useCallback(
    event => {
      //console.log("onBlur");
      if (wrapperState.isFocused) {
        setWrapperState({ ...wrapperState, isFocused: false });
      }

      if (field.props.onBlur) field.props.onBlur(event);
    },
    [field, wrapperState, setWrapperState]
  );

  const onCalendarClose = useCallback(
    event => {
      //console.log("onCalendarClose");
      if (wrapperState.isFocused) {
        setWrapperState({ ...wrapperState, isFocused: false });
      }

      if (field.props.onCalendarClose) field.props.onCalendarClose(event);
    },
    [field, wrapperState, setWrapperState]
  );

  let field_new;
  //if (field) {
  let {
    props: { inputGroupClassName },
  } = field;

  let groupProps = {};
  if (isGroup || type === "group") {
    groupProps = {
      inputGroupClassName: cx(inputGroupClassName, {
        focused: wrapperState.isFocused,
      }),
    };
  }
  field_new = useMemo(
    () =>
      [field].map((child, idx) =>
        React.cloneElement(child, {
          ...child.props,
          ...groupProps,
          ..._props,
          key: idx,
          onFocus,
          onBlur,
          ...((type === "datepicker" || type === "timepicker") && {
            onCalendarClose,
          }),
          required,
          name: name,
          ...((selected && { selected }) || { value: _value }),
          ...(onChange && { onChange }),
          ...(options && { options }),

          //onChange: onChange ? onChange : child.props.onChange,
        })
      ),
    [field, _value, selected, options, name, _props]
  );
  //}
  const children = field_new;
  return (
    <Form.Group
      controlId={controlId}
      {..._props}
      className={cx(
        DEFAULT_CLASSNAME,
        className,
        marginClassName && marginClassName,
        {
          "floating-label-active": floatLabel,
          "input-group-field": isGroup || type === "group",
          "is-invalid": isInvalid,
        }
      )}
    >
      <div className="ifx-position-relative">
        {children}
        {label && !labelHidden && (
          <Form.Label className={labelClassName} key={1}>
            {label}
          </Form.Label>
        )}
        {(type === "groupError" && (
          <IFXErrorAlert show={isInvalid}>
            {wrapperState.errorMessage}
          </IFXErrorAlert>
        )) ||
          (!externalErrorHandler && (
            <Form.Control.Feedback type="ifx-field-error invalid">
              {wrapperState.errorMessage}
            </Form.Control.Feedback>
          ))}
      </div>
    </Form.Group>
  );
});

const IFXFieldWrapper = ({
  children: field,
  marginClassName = "mb-0 mt-ifx-125",
  isFormField = true,
  type = "default",
  valueKey,
  valueName,
  onChange,
  colProps,
  isGroup,
  ruleKey,
  autoComplete = "off",
  ...props
}) => {
  //console.log("IFXFieldWrapper props", props);

  const { control } = useFormContext() || {};
  const _isFormField = (control && props.name && isFormField) || false;

  const _valueName =
    valueName ||
    (selectedValueFields.includes(type) && "selected") ||
    undefined;

  const wrappedField = useMemo(
    () =>
      (rangeFields.includes(type) && (
        <RangeFieldWrapper
          marginClassName={marginClassName}
          type={type}
          {...(type === "group" && { isGroup: true })}
          {...(!_isFormField && { onChange })}
          autoComplete={autoComplete}
          {...props}
        >
          {field}
        </RangeFieldWrapper>
      )) ||
      ((type === "autosuggest" || type === "dropdown") && (
        <RegularFieldWrapper
          marginClassName={marginClassName}
          isGroup
          type={type}
          {...(!_isFormField && { onChange })}
          autoComplete={autoComplete}
          {...props}
        >
          <AutosuggestFieldWrapper autoComplete={autoComplete} {...field.props}>
            {field}
          </AutosuggestFieldWrapper>
        </RegularFieldWrapper>
      )) || (
        <RegularFieldWrapper
          marginClassName={type === "hidden" ? "m-0" : marginClassName}
          type={type}
          {...(type === "group" && { isGroup: true })}
          {...(!_isFormField && { onChange })}
          autoComplete={autoComplete}
          {...props}
        >
          {field}
        </RegularFieldWrapper>
      ),
    [
      props,
      type,
      colProps,
      _isFormField,
      isGroup,
      marginClassName,
      field,
      onChange,
      field.props,
      _valueName,
      marginClassName,
      autoComplete,
    ]
  );
  const component = useMemo(
    () =>
      _isFormField ? (
        rangeFields.includes(type) ? (
          <RangeFieldController
            wrapperOnChange={onChange}
            valueName={_valueName}
            ruleKey={ruleKey}
            autoComplete={autoComplete}
            {...props}
          >
            {wrappedField}
          </RangeFieldController>
        ) : (
          <FieldController
            wrapperOnChange={onChange}
            valueName={_valueName}
            ruleKey={ruleKey}
            autoComplete={autoComplete}
            {...props}
          >
            {wrappedField}
          </FieldController>
        )
      ) : (
        wrappedField
      ),
    [
      _isFormField,
      _valueName,
      wrappedField,
      onChange,
      ruleKey,
      props,
      type,
      autoComplete,
    ]
  );
  return component;
};
const AutosuggestFieldWrapper = React.memo(
  ({ children: field, selected, onChange, multiple, ...props }) => {
    const isArray = multiple || (selected && Array.isArray(selected)) || false;

    const _selected = !isArray ? (selected && [selected]) || [] : selected;

    //console.log("AutosuggestFieldWrapper isArray", isArray);

    const _onChange = useCallback(
      v => {
        return (
          onChange &&
          onChange(
            (isArray && (v || [])) || (v && v.length > 0 && v[0]) || null
          )
        );
      },
      [isArray, onChange]
    );
    return React.cloneElement(field, {
      ...field.props,
      ...props,
      selected: _selected,
      multiple: multiple || field.props.multiple,
      onChange,
      ...(!multiple && { onChange: _onChange }),
    });
  }
);

IFXFieldWrapper.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  controlId: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  name: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  floatLabel: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  type: PropTypes.oneOf([
    "group",
    "default",
    "hidden",
    "numberRange",
    "groupError",
    "privateGroupError",
    ...selectedValueFields,
  ]),
  isGroup: PropTypes.bool,
  selected: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onChange: PropTypes.func,
  marginClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  colProps: PropTypes.object,
};

export default React.memo(IFXFieldWrapper);
